import { useState } from "react";
import { useQuery, useMutation, QueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserPlus, Crown, Trash2, RefreshCw, X, Plus, Pencil } from "lucide-react";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { ErrorBannerWithId } from "@/components/error-id-chip";
import { TeamMember, ToastFn, formatCents } from "./_shared";
import { TeamMemberRateRow } from "./TeamMemberRates";



// ── Team Tab ──────────────────────────────────────────────────────────────────

export function TeamTab({ vendorId, currentMemberId, queryClient, toast }: {
  vendorId: number;
  currentMemberId: number | null;
  queryClient: QueryClient;
  toast: ToastFn;
}) {
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [addForm, setAddForm] = useState({ firstName: "", lastName: "", phone: "", email: "", role: "member" as "member" | "admin" });
  const [addHourly, setAddHourly] = useState("");
  const [addTravel, setAddTravel] = useState("");
  const [addOfferings, setAddOfferings] = useState<Array<{ name: string; priceCents: number }>>([]);
  const [addOfferingName, setAddOfferingName] = useState("");
  const [addOfferingPrice, setAddOfferingPrice] = useState("");
  const [addError, setAddError] = useState("");

  const resetAddForm = () => {
    setAddForm({ firstName: "", lastName: "", phone: "", email: "", role: "member" });
    setAddHourly("");
    setAddTravel("");
    setAddOfferings([]);
    setAddOfferingName("");
    setAddOfferingPrice("");
    setAddError("");
  };
  const [transferTargetId, setTransferTargetId] = useState<string>("");
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [editRatesMemberId, setEditRatesMemberId] = useState<number | null>(null);

  const { data: members = [], isLoading } = useQuery<TeamMember[]>({
    queryKey: ["vendor", vendorId, "team"],
    queryFn: async () => {
      const r = await fetch("/api/vendor-portal/team", { credentials: "include" });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).message || `Request failed: ${r.status}`);
      return r.json();
    },
  });

  const addMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: () => {
      const hourlyRateCents = addHourly ? Math.round(parseFloat(addHourly) * 100) : null;
      const defaultTravelFeeCents = addTravel ? Math.round(parseFloat(addTravel) * 100) : null;
      return apiRequest("POST", "/api/vendor-portal/team", {
        ...addForm,
        hourlyRateCents,
        defaultTravelFeeCents,
        flatRateOfferings: addOfferings,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vendor", vendorId, "team"] });
      toast({ title: "Team member invited", description: `${addForm.firstName} will receive an invite SMS.` });
      setShowAddDialog(false);
      resetAddForm();
    },
    onError: (e: Error) => setAddError(parseErrorMessage(e, "Failed to add team member")),
  });

  const removeMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: (memberId: number) => apiRequest("PATCH", `/api/vendor-portal/team/${memberId}`, { status: "removed" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vendor", vendorId, "team"] });
      toast({ title: "Team member removed" });
    },
    onError: (e: Error) => toast({ title: "Error", description: parseErrorMessage(e, "Failed to remove team member"), variant: "destructive" }),
  });

  const roleMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: ({ memberId, role }: { memberId: number; role: "admin" | "member" }) =>
      apiRequest("PATCH", `/api/vendor-portal/team/${memberId}`, { role }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vendor", vendorId, "team"] });
      toast({ title: "Role updated" });
    },
    onError: (e: Error) => toast({ title: "Error", description: parseErrorMessage(e, "Failed to update role"), variant: "destructive" }),
  });

  const transferMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: () => apiRequest("POST", "/api/vendor-portal/team/transfer-ownership", { newOwnerId: parseInt(transferTargetId, 10) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vendor", vendorId, "team"] });
      toast({ title: "Ownership transferred" });
      setShowTransferDialog(false);
    },
    onError: (e: Error) => toast({ title: "Error", description: parseErrorMessage(e, "Failed to transfer ownership"), variant: "destructive" }),
  });

  const activeMembers = members.filter(m => m.status !== "removed");
  const eligibleForTransfer = activeMembers.filter(m => m.role !== "owner" && m.status === "active");

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-gray-900">Team Members</h3>
        <Button size="sm" onClick={() => setShowAddDialog(true)} data-testid="button-add-team-member" data-tour="team-add-member">
          <UserPlus className="h-4 w-4 mr-1" />Add Member
        </Button>
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-400 text-center py-8" data-tour="team-members-list">Loading team...</p>
      ) : activeMembers.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-8" data-tour="team-members-list">No team members yet. Add members to delegate tasks.</p>
      ) : (
        <div className="space-y-3" data-tour="team-members-list">
          {activeMembers.map(member => (
            <Card key={member.id} data-testid={`card-team-member-${member.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                      {member.role === "owner" ? (
                        <Crown className="h-4 w-4 text-blue-600" />
                      ) : (
                        <span className="text-sm font-medium text-blue-600">
                          {member.firstName[0]}{member.lastName[0]}
                        </span>
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 text-sm">{member.firstName} {member.lastName}</p>
                      <p className="text-xs text-gray-500">{member.phone}</p>
                      {member.email && <p className="text-xs text-gray-400">{member.email}</p>}
                      {(() => {
                        const parts: string[] = [];
                        if (member.hourlyRateCents != null) parts.push(`${formatCents(member.hourlyRateCents)}/hr`);
                        if (member.defaultTravelFeeCents != null) parts.push(`${formatCents(member.defaultTravelFeeCents)} trip fee`);
                        const offeringCount = Array.isArray(member.flatRateOfferings) ? member.flatRateOfferings.length : 0;
                        if (offeringCount > 0) parts.push(`${offeringCount} flat-rate ${offeringCount === 1 ? "offering" : "offerings"}`);
                        return parts.length > 0 ? (
                          <p className="text-xs text-gray-600 mt-0.5" data-testid={`text-member-rates-${member.id}`}>{parts.join(" · ")}</p>
                        ) : (
                          <p className="text-xs text-gray-300 italic mt-0.5" data-testid={`text-member-rates-${member.id}`}>No rates set</p>
                        );
                      })()}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {member.role === "owner" ? (
                      <Badge variant="default" className="text-xs">Owner</Badge>
                    ) : member.status === "invited" ? (
                      <Badge variant="secondary" className="text-xs">Invited</Badge>
                    ) : (
                      <Select
                        value={member.role === "admin" ? "admin" : "member"}
                        onValueChange={(v: "admin" | "member") => roleMutation.mutate({ memberId: member.id, role: v })}
                        disabled={roleMutation.isPending}
                      >
                        <SelectTrigger className="w-28 h-7 text-xs" data-testid={`select-role-${member.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="member">Member</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 px-2 text-xs text-gray-500 hover:text-gray-900"
                      onClick={() => setEditRatesMemberId(member.id)}
                      data-testid={`button-edit-rates-${member.id}`}
                    >
                      <Pencil className="h-3.5 w-3.5 mr-1" />Edit rates
                    </Button>
                    {member.role !== "owner" && member.id !== currentMemberId && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                        disabled={removeMutation.isPending}
                        onClick={() => removeMutation.mutate(member.id)}
                        data-testid={`button-remove-member-${member.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {eligibleForTransfer.length > 0 && (
        <div className="pt-2" data-tour="team-transfer-ownership">
          <Button
            variant="outline"
            size="sm"
            className="w-full text-amber-700 dark:text-amber-300 border-amber-300 dark:border-amber-700 hover:bg-amber-50 dark:hover:bg-amber-950/40"
            onClick={() => setShowTransferDialog(true)}
            data-testid="button-transfer-ownership"
          >
            <RefreshCw className="h-3.5 w-3.5 mr-1" />Transfer Ownership
          </Button>
        </div>
      )}

      {/* Edit Rates Dialog */}
      <Dialog open={editRatesMemberId != null} onOpenChange={(open) => { if (!open) setEditRatesMemberId(null); }}>
        <DialogContent className="flex flex-col max-h-[90dvh]">
          <DialogHeader>
            <DialogTitle>Edit Rates</DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto flex-1">
            {(() => {
              const editing = activeMembers.find(m => m.id === editRatesMemberId);
              if (!editing) return null;
              return (
                <TeamMemberRateRow
                  member={editing}
                  vendorId={vendorId}
                  queryClient={queryClient}
                  toast={toast}
                  onSaved={() => setEditRatesMemberId(null)}
                />
              );
            })()}
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Member Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="flex flex-col max-h-[90dvh]">
          <DialogHeader>
            <DialogTitle>Invite Team Member</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 overflow-y-auto flex-1">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>First Name</Label>
                <Input
                  value={addForm.firstName}
                  onChange={e => setAddForm(f => ({ ...f, firstName: e.target.value }))}
                  placeholder="Jane"
                  data-testid="input-member-first-name"
                />
              </div>
              <div>
                <Label>Last Name</Label>
                <Input
                  value={addForm.lastName}
                  onChange={e => setAddForm(f => ({ ...f, lastName: e.target.value }))}
                  placeholder="Smith"
                  data-testid="input-member-last-name"
                />
              </div>
            </div>
            <div>
              <Label>Phone Number</Label>
              <Input
                value={addForm.phone}
                onChange={e => setAddForm(f => ({ ...f, phone: e.target.value }))}
                placeholder="(555) 123-4567"
                type="tel"
                data-testid="input-member-phone"
              />
            </div>
            <div>
              <Label>Email (optional)</Label>
              <Input
                value={addForm.email}
                onChange={e => setAddForm(f => ({ ...f, email: e.target.value }))}
                placeholder="jane@example.com"
                type="email"
                data-testid="input-member-email"
              />
            </div>
            <div>
              <Label>Role</Label>
              <Select value={addForm.role} onValueChange={(v: "member" | "admin") => setAddForm(f => ({ ...f, role: v }))}>
                <SelectTrigger data-testid="select-member-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="member">Member — handles assigned work only</SelectItem>
                  <SelectItem value="admin">Admin — can also manage host property assignments</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-3 rounded-md border border-gray-200 dark:border-gray-700 p-3">
              <p className="text-xs font-medium text-gray-600 dark:text-gray-300">Rates (optional)</p>
              <p className="text-xs text-gray-400">Set now or edit later from the Pay tab.</p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label htmlFor="input-add-member-hourly" className="text-xs">Hourly Rate ($)</Label>
                  <div className="relative mt-1">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                    <Input
                      id="input-add-member-hourly"
                      type="number"
                      min="0"
                      step="0.01"
                      className="pl-5 h-8 text-sm"
                      placeholder="0.00"
                      value={addHourly}
                      onChange={e => setAddHourly(e.target.value)}
                      data-testid="input-add-member-hourly"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="input-add-member-travel" className="text-xs">Trip Fee ($)</Label>
                  <div className="relative mt-1">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                    <Input
                      id="input-add-member-travel"
                      type="number"
                      min="0"
                      step="0.01"
                      className="pl-5 h-8 text-sm"
                      placeholder="0.00"
                      value={addTravel}
                      onChange={e => setAddTravel(e.target.value)}
                      data-testid="input-add-member-travel"
                    />
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Flat-rate Offerings</Label>
                {addOfferings.map((offering, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm" data-testid={`row-add-member-offering-${idx}`}>
                    <span className="flex-1 truncate">{offering.name}</span>
                    <span className="text-gray-600 font-medium">${(offering.priceCents / 100).toFixed(2)}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 text-red-400 hover:text-red-600"
                      onClick={() => setAddOfferings(prev => prev.filter((_, i) => i !== idx))}
                      data-testid={`button-remove-add-member-offering-${idx}`}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
                <div className="flex gap-2">
                  <Input
                    type="text"
                    placeholder="Service name"
                    value={addOfferingName}
                    onChange={e => setAddOfferingName(e.target.value)}
                    className="flex-1 h-8 text-sm"
                    data-testid="input-add-member-offering-name"
                  />
                  <div className="relative w-24">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      value={addOfferingPrice}
                      onChange={e => setAddOfferingPrice(e.target.value)}
                      className="pl-5 h-8 text-sm"
                      data-testid="input-add-member-offering-price"
                    />
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-8"
                    disabled={!addOfferingName.trim() || !addOfferingPrice}
                    onClick={() => {
                      const priceCents = Math.round(parseFloat(addOfferingPrice) * 100);
                      if (isNaN(priceCents) || priceCents <= 0) return;
                      setAddOfferings(prev => [...prev, { name: addOfferingName.trim(), priceCents }]);
                      setAddOfferingName("");
                      setAddOfferingPrice("");
                    }}
                    data-testid="button-add-member-offering"
                  >
                    <Plus className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </div>
            {addError && <ErrorBannerWithId message={addError} />}
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setShowAddDialog(false)}>Cancel</Button>
              <Button
                className="flex-1"
                disabled={addMutation.isPending || !addForm.firstName || !addForm.lastName || !addForm.phone}
                onClick={() => addMutation.mutate()}
                data-testid="button-confirm-add-member"
              >
                {addMutation.isPending ? "Sending invite..." : "Send Invite"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Transfer Ownership Dialog */}
      <Dialog open={showTransferDialog} onOpenChange={(open) => { if (!open && transferMutation.isPending) return; setShowTransferDialog(open); }}>
        <DialogContent
          onEscapeKeyDown={(e) => { if (transferMutation.isPending) e.preventDefault(); }}
          onInteractOutside={(e) => { if (transferMutation.isPending) e.preventDefault(); }}
          onPointerDownOutside={(e) => { if (transferMutation.isPending) e.preventDefault(); }}
        >
          <DialogHeader>
            <DialogTitle>Transfer Ownership</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">You will lose owner privileges. This action cannot be undone without the new owner's cooperation.</p>
            <div>
              <Label>Select New Owner</Label>
              <Select value={transferTargetId} onValueChange={setTransferTargetId}>
                <SelectTrigger data-testid="select-new-owner">
                  <SelectValue placeholder="Choose a member..." />
                </SelectTrigger>
                <SelectContent>
                  {eligibleForTransfer.map(m => (
                    <SelectItem key={m.id} value={String(m.id)}>
                      {m.firstName} {m.lastName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setShowTransferDialog(false)}>Cancel</Button>
              <Button
                variant="destructive"
                className="flex-1"
                disabled={!transferTargetId || transferMutation.isPending}
                onClick={() => transferMutation.mutate()}
                data-testid="button-confirm-transfer"
              >
                {transferMutation.isPending ? "Transferring..." : "Transfer Ownership"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
